export const PROBLEMS_PAGE_LIMIT = 5000;

export interface PageRequest {
  after: number;
  limit: number;
}

export interface PageBody<T> {
  rows?: T[];
  nextAfter?: unknown;
}

export type PageResult<T> =
  | { isOk: true; rows: T[]; nextAfter: number | null }
  | { isOk: false; message: string };

export type AllPagesResult<T> = { isOk: true; rows: T[] } | { isOk: false; message: string };

/** A plain array is the reply of a server without paging: the whole list, so one final page. */
export function pageFromBody<T>(body: T[] | PageBody<T> | null): PageResult<T> {
  if (Array.isArray(body)) return { isOk: true, rows: body, nextAfter: null };
  if (!body || !Array.isArray(body.rows)) {
    return { isOk: false, message: 'the problems page has no rows list' };
  }
  const { rows, nextAfter } = body;
  if (nextAfter === null) return { isOk: true, rows, nextAfter: null };
  if (typeof nextAfter === 'number' && Number.isInteger(nextAfter)) {
    return { isOk: true, rows, nextAfter };
  }
  return { isOk: false, message: 'the problems page has a bad nextAfter' };
}

async function collectPages<T>(
  fetchPage: (request: PageRequest) => Promise<PageResult<T>>,
  after: number,
  rows: T[],
): Promise<AllPagesResult<T>> {
  const page = await fetchPage({ after, limit: PROBLEMS_PAGE_LIMIT });
  if (!page.isOk) return { isOk: false, message: page.message };
  const allRows = rows.concat(page.rows);
  if (page.nextAfter === null) return { isOk: true, rows: allRows };
  if (page.nextAfter <= after || page.rows.length === 0) {
    return {
      isOk: false,
      message: `problems paging stopped: next page after ${page.nextAfter} does not move past ${after}`,
    };
  }
  return collectPages(fetchPage, page.nextAfter, allRows);
}

/** Loads every page from after=0 until nextAfter is null; any failed page fails the whole load. */
export const fetchAllPages = <T>(
  fetchPage: (request: PageRequest) => Promise<PageResult<T>>,
): Promise<AllPagesResult<T>> => collectPages(fetchPage, 0, []);
